import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Plus, Trash2 } from 'lucide-react'

/**
 * BlockEditor — drag-and-drop list of {id, label, content} blocks.
 *
 * Props:
 *   blocks: Array<{ id: string, label: string, content: string }>
 *   onChange: (next) => void
 *   addLabel?: string — текст кнопки добавления (default: 'Add block')
 *   contentPlaceholder?: string
 */
export default function BlockEditor({
  blocks,
  onChange,
  addLabel = 'Add block',
  contentPlaceholder = 'Type details here…',
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = blocks.findIndex((b) => b.id === active.id)
    const newIndex = blocks.findIndex((b) => b.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return
    onChange(arrayMove(blocks, oldIndex, newIndex))
  }

  const updateBlock = (id, updates) => {
    onChange(blocks.map((b) => (b.id === id ? { ...b, ...updates } : b)))
  }

  const removeBlock = (id) => {
    onChange(blocks.filter((b) => b.id !== id))
  }

  const addBlock = () => {
    const newBlock = {
      id: cryptoRandomId(),
      label: '',
      content: '',
    }
    onChange([...blocks, newBlock])
  }

  return (
    <div className="space-y-3">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
          {blocks.map((block) => (
            <SortableBlock
              key={block.id}
              block={block}
              onUpdate={(updates) => updateBlock(block.id, updates)}
              onRemove={() => removeBlock(block.id)}
              contentPlaceholder={contentPlaceholder}
            />
          ))}
        </SortableContext>
      </DndContext>

      <button
        type="button"
        onClick={addBlock}
        className="w-full flex items-center justify-center gap-2 border border-dashed border-black/15 rounded-2xl py-4 text-sm font-semibold text-ink-2 hover:bg-canvas hover:text-ink-1 transition"
      >
        <Plus className="w-4 h-4" />
        {addLabel}
      </button>
    </div>
  )
}

function SortableBlock({ block, onUpdate, onRemove, contentPlaceholder }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white rounded-2xl border border-black/5 shadow-card p-4"
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="text-ink-3 hover:text-ink-1 p-1 cursor-grab active:cursor-grabbing"
          aria-label="Drag to reorder"
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <input
          type="text"
          placeholder="Block label (e.g., Responsibilities)"
          className="flex-1 text-sm font-bold text-ink-1 outline-none bg-transparent placeholder:text-ink-3"
          value={block.label}
          onChange={(e) => onUpdate({ label: e.target.value })}
        />
        <button
          type="button"
          onClick={onRemove}
          className="text-ink-3 hover:text-red-500 p-1.5 rounded hover:bg-red-50 transition"
          aria-label="Delete block"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <textarea
        placeholder={contentPlaceholder}
        className="mt-3 w-full text-[14px] text-ink-1 outline-none bg-transparent resize-none min-h-[100px] placeholder:text-ink-3"
        value={block.content}
        onChange={(e) => onUpdate({ content: e.target.value })}
        rows={4}
      />
    </div>
  )
}

function cryptoRandomId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `id-${Math.random().toString(36).slice(2)}-${Date.now()}`
}
